import React from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { BillLine, BillingConfig, CatalogueItem } from '../../types/billing';
import { blankLine, calculateLine, formatMoney, lineFromItem } from '../../utils/billingMoney';
type Props = {
  lines: BillLine[];
  onChange: (lines: BillLine[]) => void;
  config: BillingConfig;
  items: CatalogueItem[];
  canEditPrices: boolean;
  maxDiscount: number;
};

/** A line counts once it has an item, a description or a price. Blank lines are never saved. */
export const isFilledLine = (l: BillLine) => Boolean(l.itemId || l.description.trim() || l.unitPrice > 0);

/** Categories shown on the receipt: configured ones plus any used by items. */
export const receiptCategories = (config: BillingConfig, items: CatalogueItem[]) => [...new Set([...config.catalogue.categories, ...items.filter(i => i.status === 'Active').map(i => i.category)])].filter(Boolean);

/** One blank line per category to start a receipt. */
export const initialCategoryLines = (config: BillingConfig, items: CatalogueItem[]): BillLine[] => receiptCategories(config, items).map(category => ({
  ...blankLine(config),
  category
}));
const cell = 'w-full rounded border border-slate-300 px-2 py-1.5 text-sm';

/** Receipt lines grouped by category, each with blank lines and "Add line". */
export function CategoryLinesEditor({
  lines,
  onChange,
  config,
  items,
  canEditPrices,
  maxDiscount
}: Props) {
  const fmt = (v: number) => formatMoney(v, config.regional.currencySymbol);
  const categories = [...new Set([...receiptCategories(config, items), ...lines.map(l => l.category ?? 'Other')])];
  const replace = (id: string, next: BillLine) => onChange(lines.map(l => l.id === id ? next : l));
  const update = (line: BillLine, patch: Partial<BillLine>) => {
    const next = {
      ...line,
      ...patch
    };
    if (patch.discountPct !== undefined) next.discountPct = Math.min(maxDiscount, Math.max(0, Number(patch.discountPct) || 0));
    replace(line.id, calculateLine(next));
  };
  const pickItem = (line: BillLine, itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return replace(line.id, {
      ...blankLine(config),
      id: line.id,
      category: line.category
    });
    replace(line.id, calculateLine({
      ...lineFromItem(config, item),
      id: line.id,
      category: line.category,
      quantity: line.quantity || 1
    }));
  };
  const addLine = (category: string) => {
    const lastIndex = lines.map(l => l.category ?? 'Other').lastIndexOf(category);
    const next = [...lines];
    next.splice(lastIndex + 1, 0, {
      ...blankLine(config),
      category
    });
    onChange(next);
  };
  const remove = (line: BillLine) => {
    const sameCategory = lines.filter(l => (l.category ?? 'Other') === (line.category ?? 'Other'));
    // Keep at least one (blank) line per category.
    if (sameCategory.length === 1) return replace(line.id, {
      ...blankLine(config),
      id: line.id,
      category: line.category
    });
    onChange(lines.filter(l => l.id !== line.id));
  };
  return <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-id="element-3052" data-mp="2bzh5dwesvmcm">
      <div className="overflow-x-auto" data-id="element-3053" data-mp="2c1lkzbm2a9sq">
        <table className="min-w-full text-sm" data-id="element-3054" data-mp="2gy2945t9go5t">
          <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-3055" data-mp="2gyu2d9zdy9sx">
            <tr data-id="element-3056" data-mp="2m01a591j38zl">
              <th className="min-w-[200px] px-2 py-2" data-id="element-3057" data-mp="2pz0earffr1pz">Item</th>
              <th className="min-w-[200px] px-2 py-2" data-id="element-3058" data-mp="2q0l4k4upvyi1">Description</th>
              <th className="w-20 px-2 py-2 text-right" data-id="element-3059" data-mp="2uxjzuqq4abht">Qty</th>
              <th className="w-28 px-2 py-2 text-right" data-id="element-3060" data-mp="2zx6f15pia1t9">Unit price</th>
              <th className="w-20 px-2 py-2 text-right" data-id="element-3061" data-mp="2zvd3bc01cree">Disc %</th>
              <th className="w-28 px-2 py-2 text-right" data-id="element-3062" data-mp="34phanwd7r1g0">Amount</th>
              <th className="w-10 px-2 py-2" data-id="element-3063" data-mp="39nr8yfu8ii5u"><span className="sr-only" data-id="element-3064" data-mp="39ly1iybsrtws">Remove</span></th>
            </tr>
          </thead>
          {categories.map(category => {
          const catLines = lines.filter(l => (l.category ?? 'Other') === category);
          const catItems = items.filter(i => i.status === 'Active' && i.category === category).sort((a, b) => a.name.localeCompare(b.name));
          const subtotal = catLines.filter(isFilledLine).reduce((s, l) => s + l.total, 0);
          return <tbody key={category} className="border-t border-slate-200" data-id="element-3065" data-mp="3er9xqz80iepo">
                <tr className="bg-slate-50/70" data-id="element-3066" data-mp="39s7388pnrqvy">
                  <th colSpan={5} scope="rowgroup" className="px-2 py-1.5 text-left text-xs font-bold uppercase tracking-wide text-slate-700" data-id="element-3067" data-mp="39sgvk2y6ea4t">{category}</th>
                  <td className="px-2 py-1.5 text-right text-xs font-semibold tabular-nums text-slate-600" data-id="element-3068" data-mp="2i19c392xwecl">{subtotal > 0 ? fmt(Math.round(subtotal * 100) / 100) : ''}</td>
                  <td data-id="element-3069" data-mp="2vy61d63etnrd" />
                </tr>
                {catLines.map((line, i) => {
              const item = items.find(it => it.id === line.itemId);
              const priceLocked = Boolean(line.itemId) && !canEditPrices;
              const freeLocked = !line.itemId && !canEditPrices;
              const label = `${category} line ${i + 1}`;
              return <tr key={line.id} className="border-t border-slate-100 align-top" data-id="element-3070" data-mp="1ob6dkaudbgu5" data-mpi={i}>
                      <td className="px-2 py-1.5" data-id="element-3071" data-mp="1t5tr5enu2qty">
                        <label htmlFor={`cl-item-${line.id}`} className="sr-only" data-id="element-3072" data-mp="1f8x4lpeqzviv">{label} item</label>
                        <select id={`cl-item-${line.id}`} value={line.itemId ?? ''} onChange={e => pickItem(line, e.target.value)} className={`${cell} bg-white`} data-id="element-3073" data-mp="1k6giupbg48at">
                          <option value="" data-id="element-3074" data-mp="2byeg4l0ewbsh">{catItems.length ? '— Select item —' : 'No items in this category'}</option>
                          {catItems.map(it => <option key={it.id} value={it.id} data-id="element-3075" data-mp="1y5ycbhzos9qi">{it.code} · {it.name}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5" data-id="element-3076" data-mp="233rfs39lupn3">
                        <label htmlFor={`cl-desc-${line.id}`} className="sr-only" data-id="element-3077" data-mp="2quwtocvzj7ur">{label} description</label>
                        <input id={`cl-desc-${line.id}`} value={line.description} readOnly={freeLocked} onChange={e => update(line, {
                    description: e.target.value
                  })} placeholder={freeLocked ? 'Select an item' : 'Blank line — type a description'} className={`${cell} ${freeLocked ? 'bg-slate-50' : ''}`} data-id="element-3078" data-mp="2vnyc2uvehpj3" />
                      </td>
                      <td className="px-2 py-1.5" data-id="element-3079" data-mp="2vm0xeybrhgzu">
                        <label htmlFor={`cl-qty-${line.id}`} className="sr-only" data-id="element-3080" data-mp="2qk37jl8uvnxx">{label} quantity</label>
                        <input id={`cl-qty-${line.id}`} type="number" min="0" step="1" value={line.quantity} onChange={e => update(line, {
                    quantity: Number(e.target.value)
                  })} className={`${cell} text-right tabular-nums`} data-id="element-3081" data-mp="2mkb6dtlskk2l" />
                      </td>
                      <td className="px-2 py-1.5" data-id="element-3082" data-mp="27un5gthivnfe">
                        <label htmlFor={`cl-price-${line.id}`} className="sr-only" data-id="element-3083" data-mp="27vy98taywv6i">{label} unit price</label>
                        <input id={`cl-price-${line.id}`} type="number" min="0" step="0.01" value={line.unitPrice || ''} readOnly={priceLocked || freeLocked} onChange={e => update(line, {
                    unitPrice: Number(e.target.value)
                  })} placeholder="0.00" className={`${cell} text-right tabular-nums ${priceLocked || freeLocked ? 'bg-slate-50 text-slate-600' : ''}`} data-id="element-3084" data-mp="22v0me3f0jcyo" />
                      </td>
                      <td className="px-2 py-1.5" data-id="element-3085" data-mp="ndw10n21c0dd">
                        <label htmlFor={`cl-disc-${line.id}`} className="sr-only" data-id="element-3086" data-mp="sbnv9nq5jykq">{label} discount percent</label>
                        <input id={`cl-disc-${line.id}`} type="number" min="0" max={maxDiscount} step="0.5" value={line.discountPct || ''} readOnly={maxDiscount === 0 || item?.discountAllowed === false} onChange={e => update(line, {
                    discountPct: Number(e.target.value)
                  })} placeholder="0" className={`${cell} text-right tabular-nums ${maxDiscount === 0 || item?.discountAllowed === false ? 'bg-slate-50 text-slate-500' : ''}`} data-id="element-3087" data-mp="3qttcczbxneda" />
                      </td>
                      <td className="px-2 py-1.5 pt-3 text-right font-semibold tabular-nums" data-id="element-3088" data-mp="380phxbqw4xhm">{isFilledLine(line) ? fmt(line.total) : <span className="text-slate-300" data-id="element-3089" data-mp="3czjx83png3wz">—</span>}</td>
                      <td className="px-2 py-1.5" data-id="element-3090" data-mp="26f422juq7a9g">
                        <button type="button" onClick={() => remove(line)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label={`Clear or remove ${label}`} data-id="element-3091" data-mp="21fr5v8hysqu7"><Trash2Icon className="h-4 w-4" data-id="element-3092" data-mp="2fd5xmjvoxapc" /></button>
                      </td>
                    </tr>;
            })}
                <tr data-id="element-3093" data-mp="2pcn2iz34bkk9">
                  <td colSpan={7} className="px-2 pb-2" data-id="element-3094" data-mp="2pbvimlivvb9t">
                    <button type="button" onClick={() => addLine(category)} className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50" data-id="element-3095" data-mp="2kelncp8tc5e2">
                      <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-3096" data-mp="2u4ey5o14sgh9" /> Add line to {category}
                    </button>
                  </td>
                </tr>
              </tbody>;
        })}
        </table>
      </div>
      <p className="border-t border-slate-100 bg-slate-50/60 px-3 py-2 text-xs text-slate-500" data-id="element-3097" data-mp="33663grrrelal">Blank lines are left off the receipt. {canEditPrices ? 'Type into a blank line to bill something that is not in the item list.' : 'Your role bills items from the list only.'}</p>
    </div>;
}